import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Ban,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  FileArchive,
  Link2,
  Link2Off,
  Mail,
  MailX,
  PackagePlus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/use-auth';
import {
  api,
  ApiError,
  type ExportAccessLogEntry,
  type ExportHistoryItem,
  type ExportRequestSummary,
  type ExportStatus,
  type ExportSummary,
  type IssuedExportLink,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import {
  daysUntil,
  formatBytes,
  formatDateTime,
  formatNumber,
  formatRelative,
  formatUntil,
} from '../lib/format';

const itemAt = (item: ExportHistoryItem): string =>
  item.kind === 'export' ? item.built_at : item.requested_at;

const getExportValue = (item: ExportHistoryItem, key: string): unknown => {
  switch (key) {
    case 'client_id':
      return item.client_id;
    case 'at':
      return itemAt(item);
    case 'status':
      return item.status;
    case 'requested_by':
      return item.requested_by ?? '';
    case 'zip_size_bytes':
      return item.kind === 'export' ? (item.zip_size_bytes ?? 0) : -1;
    case 'archive_count':
      return item.kind === 'export' ? item.archive_count : -1;
    default:
      return undefined;
  }
};

export function Exporter() {
  const { can } = useAuth();
  const canRun = can('exporter.run');
  const canDelete = can('exporter.delete');
  const queryClient = useQueryClient();

  const exports = useInfiniteQuery({
    queryKey: ['exporter', 'exports'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.listExports(pageParam ? { cursor: pageParam } : {}),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30 * 1000,
    // A queued export becomes a built one when copy-zip finishes; keep the
    // page honest about it without a manual refresh.
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.items.some((i) => i.status === 'running'))
        ? 15_000
        : false,
  });

  // Valid client ids are the datalake's partition clients — the same names the
  // Coverage matrix shows, but listed from the lake's folders rather than by
  // reading a year of partitions, which outran API Gateway's 30s limit.
  const clients = useQuery({
    queryKey: ['exporter', 'clients'],
    queryFn: () => api.getDatalakeClients(),
    staleTime: 5 * 60 * 1000,
    enabled: canRun,
  });

  const deleteMutation = useMutation({
    mutationFn: (item: ExportSummary) => api.deleteExport(item.client_id, item.export_timestamp),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['exporter', 'exports'] });
      void queryClient.invalidateQueries({ queryKey: ['exporter', 'links'] });
    },
  });

  const items = exports.data?.pages.flatMap((p) => p.items) ?? [];
  const { sortedItems, sort, toggle } = useSort(items, getExportValue);
  const [selected, setSelected] = useState<ExportSummary | null>(null);
  const retentionDays = exports.data?.pages[0]?.retention_days ?? null;
  const columnCount = 8;

  const confirmDelete = (item: ExportSummary) => {
    const lines = [
      `Permanently delete the ${item.client_id} export from ${item.export_timestamp}?`,
      '',
      `${formatNumber(item.archive_count + 1)} archives · ${formatBytes(item.zip_size_bytes)} zip`,
      '',
      'Any download link already sent to the client will stop working immediately.',
      'This cannot be undone — the export would have to be re-run. It stays in the history as deleted.',
    ];
    if (confirm(lines.join('\n'))) deleteMutation.mutate(item);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Data Exporter</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Bundle a client's data into per-year archives and generate secure download links.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => void exports.refetch()}
          disabled={exports.isFetching}
        >
          <RefreshCw className={exports.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Refresh
        </Button>
      </div>

      {canRun && (
        <TriggerCard
          clients={clients.data?.clients ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.error ? (clients.error as Error).message : null}
          onQueued={() => void exports.refetch()}
        />
      )}

      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileArchive className="h-4 w-4 text-muted-foreground" />
            Export history
          </CardTitle>
        </CardHeader>
        {exports.error && (
          <div className="px-6 pb-3 text-sm text-destructive">
            {(exports.error as Error).message}
          </div>
        )}
        {deleteMutation.isError && (
          <div className="px-6 pb-3 text-sm text-destructive">
            {messageOf(deleteMutation.error)}
          </div>
        )}
        {deleteMutation.isSuccess && (
          <div className="px-6 pb-3 text-sm text-success">
            Deleted {formatNumber(deleteMutation.data.objects_deleted)} objects (
            {formatBytes(deleteMutation.data.bytes_freed)} freed
            {deleteMutation.data.links_revoked
              ? `, ${formatNumber(deleteMutation.data.links_revoked)} link${deleteMutation.data.links_revoked === 1 ? '' : 's'} revoked`
              : ''}
            ).
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-y border-border">
              <tr>
                <SortableHeader label="Client" sortKey="client_id" sort={sort} onToggle={toggle} />
                <SortableHeader label="Exported" sortKey="at" sort={sort} onToggle={toggle} />
                <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Requested by"
                  sortKey="requested_by"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader label="Years" sortKey="archive_count" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Zip size"
                  sortKey="zip_size_bytes"
                  sort={sort}
                  onToggle={toggle}
                />
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Archive</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {exports.isLoading && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedItems.map((item) =>
                item.kind === 'request' ? (
                  <RequestRow key={`${item.client_id}/${item.requested_at}`} item={item} />
                ) : (
                  <ExportRow
                    key={`${item.client_id}/${item.export_timestamp}`}
                    item={item}
                    canDelete={canDelete}
                    deleting={
                      deleteMutation.isPending &&
                      deleteMutation.variables?.client_id === item.client_id &&
                      deleteMutation.variables?.export_timestamp === item.export_timestamp
                    }
                    deleteDisabled={deleteMutation.isPending}
                    onOpen={() => setSelected(item)}
                    onDelete={() => confirmDelete(item)}
                  />
                ),
              )}
              {!exports.isLoading && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-6 text-center text-muted-foreground">
                    No exports yet. Start one above to build the first archive.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 text-xs text-muted-foreground border-t border-border space-y-2">
          {exports.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void exports.fetchNextPage()}
                disabled={exports.isFetchingNextPage}
              >
                {exports.isFetchingNextPage ? 'Loading…' : 'Older'}
              </Button>
            </div>
          )}
          {retentionDays !== null && (
            <p>
              Archives are removed automatically {retentionDays} days after they are built; the
              export stays in this history for good. Deleting one early frees the storage and
              immediately breaks any link already sent for it.
            </p>
          )}
        </div>
      </Card>

      <RecentLinkActivity />

      {selected && (
        <LinksDrawer
          item={selected}
          canRun={canRun}
          onClose={() => setSelected(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['exporter', 'exports'] })}
        />
      )}
    </div>
  );
}

const STATUS_BADGE: Record<
  ExportStatus,
  { label: string; variant: 'success' | 'info' | 'destructive' | 'secondary' | 'outline' }
> = {
  available: { label: 'Available', variant: 'success' },
  running: { label: 'Building', variant: 'info' },
  'no-archive': { label: 'No archive', variant: 'destructive' },
  expired: { label: 'Expired', variant: 'secondary' },
  deleted: { label: 'Deleted', variant: 'outline' },
};

function ExportStatusBadge({ status }: { status: ExportStatus }) {
  const { label, variant } = STATUS_BADGE[status];
  return <Badge variant={variant}>{label}</Badge>;
}

function RequestRow({ item }: { item: ExportRequestSummary }) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-3 font-medium text-foreground">{item.client_id}</td>
      <td className="px-4 py-3">
        <div className="text-foreground">{formatRelative(item.requested_at)}</div>
        <div className="text-xs text-muted-foreground">requested</div>
      </td>
      <td className="px-4 py-3">
        <ExportStatusBadge status={item.status} />
      </td>
      <td className="px-4 py-3 font-mono text-xs">{item.requested_by}</td>
      <td colSpan={4} className="px-4 py-3 text-xs text-muted-foreground">
        {item.status === 'running'
          ? 'Building the archive — this row updates when it lands.'
          : 'No archive appeared within an hour of the request. Check the copy-zip Lambda logs, then run it again.'}
      </td>
    </tr>
  );
}

function ExportRow({
  item,
  canDelete,
  deleting,
  deleteDisabled,
  onOpen,
  onDelete,
}: {
  item: ExportSummary;
  canDelete: boolean;
  deleting: boolean;
  deleteDisabled: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const gone = item.status !== 'available';
  return (
    <tr
      onClick={onOpen}
      className={`border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer ${gone ? 'text-muted-foreground' : ''}`}
    >
      <td className={`px-4 py-3 font-medium ${gone ? '' : 'text-foreground'}`}>{item.client_id}</td>
      <td className="px-4 py-3">
        <div className={gone ? '' : 'text-foreground'}>{formatRelative(item.built_at)}</div>
        <div className="text-xs text-muted-foreground font-mono">{item.export_timestamp}</div>
      </td>
      <td className="px-4 py-3">
        <ExportStatusBadge status={item.status} />
      </td>
      <td className="px-4 py-3 font-mono text-xs">
        {item.requested_by ?? <span className="font-sans text-muted-foreground">not recorded</span>}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {item.years.length > 0 ? (
            item.years.map((y) => (
              <Badge key={y} variant="secondary">
                {y}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground">{item.archive_count}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 tabular-nums text-muted-foreground">
        {formatBytes(item.zip_size_bytes)}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        <ArchiveCell item={item} />
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <span className="inline-flex items-center gap-1 text-xs text-primary">
          <Link2 className="h-3.5 w-3.5" />
          Links
        </span>
        {canDelete && !gone && (
          <Button
            variant="ghost"
            size="sm"
            disabled={deleteDisabled}
            aria-label={`Delete ${item.client_id} export ${item.export_timestamp}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="ml-2 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className={deleting ? 'h-4 w-4 animate-pulse' : 'h-4 w-4'} />
          </Button>
        )}
      </td>
    </tr>
  );
}

function ArchiveCell({ item }: { item: ExportSummary }) {
  if (item.status === 'deleted') {
    return (
      <div>
        <div>Deleted {item.gone_at ? formatRelative(item.gone_at) : ''}</div>
        <div className="text-xs">{item.deleted_by ?? ''}</div>
      </div>
    );
  }
  if (item.status === 'expired') {
    const at = item.gone_at ?? item.expires_at;
    return (
      <div>
        <div>Expired {formatRelative(at)}</div>
        <div className="text-xs">{formatDateTime(at)}</div>
      </div>
    );
  }
  const remaining = daysUntil(item.expires_at);
  return (
    <div>
      <div className={remaining !== null && remaining <= 3 ? 'text-warning' : ''}>
        Deletes {remaining !== null && remaining <= 0 ? 'any moment' : formatUntil(item.expires_at)}
      </div>
      <div className="text-xs text-muted-foreground">{formatDateTime(item.expires_at)}</div>
    </div>
  );
}

function TriggerCard({
  clients,
  clientsLoading,
  clientsError,
  onQueued,
}: {
  clients: string[];
  clientsLoading: boolean;
  clientsError: string | null;
  onQueued: () => void;
}) {
  const [clientId, setClientId] = useState('');
  const trimmed = clientId.trim();
  const valid = trimmed.length > 0 && !trimmed.includes('/');

  const mutation = useMutation({
    mutationFn: (id: string) => api.triggerExport(id),
    onSuccess: () => onQueued(),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PackagePlus className="h-4 w-4 text-muted-foreground" />
          Start a new export
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          className="flex items-start gap-2 flex-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) mutation.mutate(trimmed);
          }}
        >
          <div className="flex-1 min-w-[16rem]">
            <Input
              list="exporter-clients"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={clientsLoading ? 'Loading clients…' : 'Select or type a client_id…'}
              aria-label="Client"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <datalist id="exporter-clients">
              {clients.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <Button type="submit" disabled={!valid || mutation.isPending}>
            <Download className="h-4 w-4" />
            {mutation.isPending ? 'Queuing…' : 'Start export'}
          </Button>
        </form>

        <p className="text-xs text-muted-foreground">
          Suggestions come from the datalake coverage data — but you can type any client_id to
          override (e.g. one that isn't ingested into the datalake).
        </p>

        {trimmed && !valid && (
          <p className="text-xs text-destructive">client_id can't contain a slash.</p>
        )}

        {clientsError && <p className="text-sm text-destructive">{clientsError}</p>}

        {mutation.isError && (
          <p className="text-sm text-destructive">{messageOf(mutation.error)}</p>
        )}

        {mutation.isSuccess && (
          <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm flex gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <div>
              <p className="font-medium text-foreground">
                Export queued for <span className="font-mono">{mutation.data.client_id}</span>.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                The job runs in the background (typically a few minutes; longer for many years of
                data). It'll appear in the list below once the archive is written — hit Refresh to
                check.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Page-level answer to "what did we send, to whom, and when" — every issuance
 * across every export, newest first, rather than one export at a time.
 */
function RecentLinkActivity() {
  const query = useQuery({
    queryKey: ['exporter', 'links', 'recent'],
    queryFn: () => api.listRecentExportLinks(50),
    staleTime: 30 * 1000,
  });

  const items = query.data?.items ?? [];
  const emailed = items.filter((i) => i.email);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          Link &amp; email activity
        </CardTitle>
      </CardHeader>

      {query.error && (
        <div className="px-6 pb-3 text-sm text-destructive">{(query.error as Error).message}</div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-y border-border">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Issued</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Export</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">By</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Emailed to</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Link status</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {items.map((link) => (
              <tr key={`${link.exportId}#${link.issuedAt}`} className="border-b border-border last:border-0">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-foreground">{formatRelative(link.timestamp)}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(link.timestamp)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-foreground">{link.clientId}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {link.exportTimestamp}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{link.issuedBy}</td>
                <td className="px-4 py-3">
                  <EmailCell email={link.email} />
                </td>
                <td className="px-4 py-3">
                  <LinkStatusBadge link={link} />
                </td>
              </tr>
            ))}
            {!query.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  No download links have been issued yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-3 text-xs text-muted-foreground border-t border-border space-y-1">
        {items.length > 0 && (
          <p>
            {formatNumber(emailed.length)} of the {formatNumber(items.length)} issuances shown were
            emailed
            {query.data?.truncated && query.data.scanned_through
              ? `; searched back to ${query.data.scanned_through}`
              : ''}
            .
          </p>
        )}
        <p>
          Status is what SES accepted at send time. Open an export to see whether its archives were
          actually downloaded, and to copy a link that is still valid.
        </p>
      </div>
    </Card>
  );
}

function EmailCell({ email }: { email: IssuedExportLink['email'] }) {
  if (!email) {
    return <span className="text-muted-foreground">Not emailed — link copied manually</span>;
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        {email.success ? (
          <Mail className="h-3.5 w-3.5 text-success" />
        ) : (
          <MailX className="h-3.5 w-3.5 text-destructive" />
        )}
        <span className="text-foreground">{email.addresses.join(', ') || '—'}</span>
      </div>
      {email.success ? (
        <div className="text-xs text-muted-foreground font-mono truncate">
          {email.message_id ?? 'accepted by SES'}
        </div>
      ) : (
        <div className="text-xs text-destructive">
          {email.error_message ?? email.error_code ?? 'Send failed'}
        </div>
      )}
    </div>
  );
}

/**
 * True when the issuance was revoked in the portal but the URLs it handed out
 * are still inside their S3 expiry — i.e. still downloadable by anyone holding
 * one. Deleting the export is the only thing that changes that, so a flat
 * "revoked" badge would read as "dead" for a link that still works.
 */
function urlsStillLive(link: IssuedExportLink): boolean {
  return link.revokedReason === 'revoked-early' && Date.parse(link.linkExpiresAt) > Date.now();
}

function LinkStatusBadge({ link }: { link: IssuedExportLink }) {
  if (link.status === 'revoked') {
    const live = urlsStillLive(link);
    return (
      <div>
        <Badge variant={live ? 'warning' : 'destructive'}>revoked</Badge>
        {live && (
          <div className="text-xs text-warning mt-1">
            sent URLs work {formatUntil(link.linkExpiresAt)}
          </div>
        )}
      </div>
    );
  }
  if (link.status === 'expired') {
    return (
      <div>
        <Badge variant="secondary">expired</Badge>
        <div className="text-xs text-muted-foreground mt-1">
          {formatRelative(link.linkExpiresAt)}
        </div>
      </div>
    );
  }
  return (
    <div>
      <Badge variant="success">active</Badge>
      <div className="text-xs text-muted-foreground mt-1">
        expires {formatUntil(link.linkExpiresAt)}
      </div>
    </div>
  );
}

const EXPIRY_OPTIONS = [
  { label: '24 hours', value: 24 * 3600 },
  { label: '72 hours', value: 72 * 3600 },
  { label: '7 days', value: 7 * 24 * 3600 },
];

function LinksDrawer({
  item,
  canRun,
  onClose,
  onChanged,
}: {
  item: ExportSummary;
  canRun: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [expiry, setExpiry] = useState(72 * 3600);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailStyle, setEmailStyle] = useState<'simple' | 'branded'>('simple');
  const [emailsRaw, setEmailsRaw] = useState('');

  const emailAddresses = emailsRaw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const links = useQuery({
    queryKey: ['exporter', 'links', item.client_id, item.export_timestamp],
    queryFn: () => api.listExportLinks(item.client_id, item.export_timestamp),
    staleTime: 15 * 1000,
  });

  const activity = useQuery({
    queryKey: ['exporter', 'access-logs', item.client_id, item.export_timestamp],
    queryFn: () => api.getExportAccessLogs(item.client_id, item.export_timestamp),
    staleTime: 30 * 1000,
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.generateExportLinks({
        client_id: item.client_id,
        export_timestamp: item.export_timestamp,
        expiry_seconds: expiry,
        ...(sendEmail
          ? { send_email: true, email_addresses: emailAddresses, email_style: emailStyle }
          : {}),
      }),
    onSuccess: () => {
      // The issued-links list is the single rendering path for URLs, so the
      // fresh result shows up there rather than in a second, throwaway panel.
      void queryClient.invalidateQueries({ queryKey: ['exporter', 'links'] });
      onChanged();
    },
  });

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">{item.client_id}</h2>
            <p className="text-xs text-muted-foreground font-mono">{item.export_timestamp}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-3">
            <Mini label="Years" value={formatNumber(item.archive_count)} />
            <Mini label="Files" value={formatNumber(item.total_file_count)} />
            <Mini label="Zip size" value={formatBytes(item.zip_size_bytes)} />
          </div>

          <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
            <span className="text-muted-foreground">Status</span>
            <span>
              <ExportStatusBadge status={item.status} />
            </span>
            <span className="text-muted-foreground">Built</span>
            <span>{formatDateTime(item.built_at)}</span>
            <span className="text-muted-foreground">Requested by</span>
            <span className="font-mono text-[13px]">
              {item.requested_by ?? (
                <span className="font-sans text-muted-foreground">
                  not recorded — started outside the portal or before the audit log
                </span>
              )}
            </span>
            <span className="text-muted-foreground">Archive</span>
            <span>
              <ArchiveCell item={item} />
            </span>
          </div>

          {canRun && item.status !== 'available' ? (
            <p className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
              The archive is gone, so no new links can be issued. Run the export again to send this
              client a fresh copy. The links and downloads below are the record of what was sent.
            </p>
          ) : canRun ? (
            <section className="space-y-4">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                Generate download links
              </h3>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Link expiry
                </label>
                <Select value={expiry} onChange={(e) => setExpiry(Number(e.target.value))}>
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-3 rounded-md border border-border p-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email the links to the client
                </label>

                {sendEmail && (
                  <div className="space-y-3 pl-6">
                    <div className="space-y-1">
                      <label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Recipients
                      </label>
                      <Input
                        value={emailsRaw}
                        onChange={(e) => setEmailsRaw(e.target.value)}
                        placeholder="client@example.com, ops@example.com"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                      <p className="text-xs text-muted-foreground">
                        Separate multiple addresses with commas or spaces.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Template
                      </label>
                      <Select
                        value={emailStyle}
                        onChange={(e) => setEmailStyle(e.target.value === 'branded' ? 'branded' : 'simple')}
                      >
                        <option value="simple">Simple</option>
                        <option value="branded">Branded (XiltriX)</option>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending || (sendEmail && emailAddresses.length === 0)}
              >
                <Link2 className="h-4 w-4" />
                {mutation.isPending
                  ? 'Generating…'
                  : sendEmail
                    ? 'Generate links + send email'
                    : 'Generate links'}
              </Button>

              {mutation.isError && (
                <p className="text-sm text-destructive">{messageOf(mutation.error)}</p>
              )}
            </section>
          ) : (
            <p className="text-sm text-muted-foreground">
              You have view-only access to exports. Generating download links requires the
              <span className="font-medium"> Run Data Exporter</span> permission.
            </p>
          )}

          <IssuedLinks
            links={links.data?.items ?? []}
            loading={links.isLoading}
            error={links.error ? (links.error as Error).message : null}
            activity={activity.data?.items ?? []}
            canRun={canRun}
          />

          <DownloadActivity
            data={activity.data}
            loading={activity.isLoading}
            error={activity.error ? (activity.error as Error).message : null}
            links={links.data?.items ?? []}
          />
        </div>
      </div>
    </div>
  );
}

interface LinkUsage {
  downloads: number;
  ips: number;
  failures: number;
}

/**
 * S3 redacts `X-Amz-Signature` in its access logs but leaves `X-Amz-Date`, so
 * the signing timestamp recorded on an issuance is what matches its own hits.
 */
function usageOf(link: IssuedExportLink, activity: ExportAccessLogEntry[]): LinkUsage {
  const mine = link.signedAt ? activity.filter((e) => e.signed_at === link.signedAt) : [];
  const ok = mine.filter((e) => e.http_status !== null && e.http_status >= 200 && e.http_status < 300);
  return {
    // A resumed or ranged download shows up as one 200 plus several 206s, so
    // only the 200s are counted as "a download started".
    downloads: ok.filter((e) => e.http_status === 200).length,
    ips: new Set(ok.map((e) => e.remote_ip)).size,
    failures: mine.filter((e) => e.http_status !== null && e.http_status >= 400).length,
  };
}

function IssuedLinks({
  links,
  loading,
  error,
  activity,
  canRun,
}: {
  links: IssuedExportLink[];
  loading: boolean;
  error: string | null;
  activity: ExportAccessLogEntry[];
  canRun: boolean;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium flex items-center gap-2">
        <Clock className="h-4 w-4 text-muted-foreground" />
        Issued links
      </h3>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && links.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No download links have been generated for this export yet.
        </p>
      )}

      {links.map((link) => (
        <IssuedLinkCard
          key={`${link.exportId}#${link.issuedAt}`}
          link={link}
          usage={usageOf(link, activity)}
          canRun={canRun}
        />
      ))}
    </section>
  );
}

function IssuedLinkCard({
  link,
  usage,
  canRun,
}: {
  link: IssuedExportLink;
  usage: LinkUsage;
  canRun: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const hasUrls = !link.urlsRedacted && link.zipUrl !== '';

  const revoke = useMutation({
    mutationFn: () =>
      api.revokeExportLink(link.clientId, link.exportTimestamp, link.issuedAt),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['exporter', 'links'] });
    },
  });

  // Spelled out rather than softened: an operator reaching for this is usually
  // reacting to a link that went somewhere it shouldn't have, and the one thing
  // they must not walk away believing is that the URL in that inbox is dead.
  const confirmRevoke = () => {
    const lines = [
      `Revoke the download link issued ${formatDateTime(link.timestamp)}?`,
      '',
      ...(link.email?.addresses.length
        ? [`Emailed to ${link.email.addresses.join(', ')}`, '']
        : []),
      'This removes the stored URLs from the portal so they cannot be copied again,',
      'and marks the issuance revoked in the link history.',
      '',
      'It does NOT invalidate a URL already sent — S3 presigned links keep working',
      `until they expire (${formatDateTime(link.linkExpiresAt)}). To break them now,`,
      'delete the export itself.',
    ];
    if (confirm(lines.join('\n'))) revoke.mutate();
  };

  return (
    <div className="rounded-md border border-border">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <LinkStatusBadge link={link} />
            <span className="text-sm text-foreground">{formatRelative(link.timestamp)}</span>
            <span className="text-xs text-muted-foreground">by {link.issuedBy}</span>
          </div>
          <EmailCell email={link.email} />
        </div>
        <div className="shrink-0 text-right">
          <div className="text-sm font-medium tabular-nums text-foreground">
            {formatNumber(usage.downloads)}
          </div>
          <div className="text-xs text-muted-foreground">
            download{usage.downloads === 1 ? '' : 's'}
            {usage.ips > 1 ? ` · ${usage.ips} IPs` : ''}
          </div>
          {usage.failures > 0 && (
            <div className="text-xs text-warning">
              {formatNumber(usage.failures)} refused
            </div>
          )}
          {canRun && link.status === 'active' && (
            <Button
              variant="ghost"
              size="sm"
              disabled={revoke.isPending}
              onClick={confirmRevoke}
              className="mt-1 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Link2Off className={revoke.isPending ? 'h-3.5 w-3.5 animate-pulse' : 'h-3.5 w-3.5'} />
              {revoke.isPending ? 'Revoking…' : 'Revoke'}
            </Button>
          )}
        </div>
      </div>

      {revoke.isError && (
        <div className="border-t border-border px-4 py-2 text-xs text-destructive">
          {messageOf(revoke.error)}
        </div>
      )}

      {link.revokedAt && (
        <div
          className={`flex items-start gap-2 border-t border-border px-4 py-2 text-xs ${
            urlsStillLive(link) ? 'text-warning' : 'text-destructive'
          }`}
        >
          <Ban className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          {link.revokedReason === 'revoked-early' ? (
            <span>
              Revoked {formatRelative(link.revokedAt)}
              {link.revokedBy ? ` by ${link.revokedBy}` : ''} — the URLs are no longer stored here.
              {urlsStillLive(link) ? (
                <>
                  {' '}
                  A copy already sent still downloads until{' '}
                  {formatDateTime(link.linkExpiresAt)}; delete the export to break it now.
                </>
              ) : (
                ' They had already expired.'
              )}
            </span>
          ) : (
            <span>
              Export deleted {formatRelative(link.revokedAt)} — these URLs no longer resolve.
            </span>
          )}
        </div>
      )}

      {hasUrls && (
        <div className="border-t border-border px-4 py-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-primary hover:underline"
          >
            {open ? 'Hide' : 'Show'} download URLs ({formatNumber(link.archives.length + 1)})
          </button>
          {open && (
            <div className="space-y-2 pt-3">
              <CopyableLink
                label="Complete bundle (.zip)"
                url={link.zipUrl}
                sub={formatBytes(link.zipSizeBytes)}
              />
              {link.archives.map((a) => (
                <CopyableLink
                  key={a.archive_key}
                  label={`${a.year} (.tar.gz)`}
                  url={a.url}
                  sub={`${formatBytes(a.archive_size_bytes)} · ${formatNumber(a.file_count)} files`}
                />
              ))}
              <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-xs flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                Anyone holding these URLs can download the data until they expire. Share them only
                with the client.
              </div>
            </div>
          )}
        </div>
      )}

      {!hasUrls && link.status === 'active' && !canRun && (
        <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          The URLs are hidden — viewing a live download link requires the Run Data Exporter
          permission.
        </div>
      )}
    </div>
  );
}

interface AccessLogResult {
  items: ExportAccessLogEntry[];
  truncated: boolean;
  enabled: boolean;
}

function DownloadActivity({
  data,
  loading,
  error,
  links,
}: {
  data: AccessLogResult | undefined;
  loading: boolean;
  error: string | null;
  links: IssuedExportLink[];
}) {
  const issuedBySignedAt = new Map(links.filter((l) => l.signedAt).map((l) => [l.signedAt, l]));

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium flex items-center gap-2">
        <Activity className="h-4 w-4 text-muted-foreground" />
        Download activity
      </h3>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {data && data.enabled === false && (
        <p className="text-sm text-muted-foreground">
          Access logging isn't enabled on the export bucket yet, so downloads aren't being
          recorded.
        </p>
      )}

      {data?.enabled && data.items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No downloads recorded for this export yet.
        </p>
      )}

      {data?.enabled && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">When</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">File</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">From</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Link</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Sent</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e, i) => {
                  const issuance = e.signed_at ? issuedBySignedAt.get(e.signed_at) : undefined;
                  return (
                    <tr key={`${e.time}-${e.key}-${i}`} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-foreground">{formatRelative(e.time)}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {formatDateTime(e.time)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{e.key}</div>
                        {e.http_status === 206 && (
                          <Badge variant="secondary" className="mt-1">
                            partial
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={e.http_status} errorCode={e.error_code} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {e.remote_ip || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {issuance ? (
                          <>
                            <div className="text-foreground">
                              {formatRelative(issuance.timestamp)}
                            </div>
                            <div className="text-muted-foreground truncate max-w-[12rem]">
                              {issuance.email?.addresses.join(', ') ?? 'copied manually'}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">
                            {e.presigned ? 'link not on record' : 'direct S3 access'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {formatBytes(e.bytes_sent)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.truncated && (
            <p className="text-xs text-muted-foreground">
              Showing the most recent {data.items.length} entries; older activity is omitted.
            </p>
          )}
        </>
      )}

      <p className="text-xs text-muted-foreground">
        S3 delivers access logs on a delay (often a few hours), so very recent downloads may not
        appear yet. Mail-security scanners also fetch links before the recipient does, so a hit is
        not proof a person downloaded the file.
      </p>
    </section>
  );
}

function StatusBadge({ status, errorCode }: { status: number | null; errorCode: string | null }) {
  if (status == null) return <Badge variant="secondary">—</Badge>;
  if (status >= 200 && status < 300) return <Badge variant="success">{status} OK</Badge>;
  if (status === 403) {
    return <Badge variant="warning">403 {errorCode === 'AccessDenied' ? 'expired' : 'forbidden'}</Badge>;
  }
  if (status >= 500) return <Badge variant="destructive">{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function CopyableLink({ label, url, sub }: { label: string; url: string; sub?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-center gap-3 rounded-md border border-border px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">{label}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
        <div className="text-xs text-muted-foreground font-mono truncate">{url}</div>
      </div>
      <Button variant="outline" size="icon" onClick={copy} aria-label={`Copy ${label} link`}>
        {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-lg font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}
